import { useEffect, useMemo, useState } from 'react';
import type { SatelliteIndex, SatelliteObservation } from '@greeniq/shared';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { INDEX_META } from '../lib/satelliteColors.js';
import { getObservations } from '../api/satellite.js';
import {
  DARK_CHART_TOKENS,
  darkTooltipContentStyle,
  darkTooltipItemStyle,
  darkTooltipLabelStyle,
  darkLegendWrapperStyle,
} from '../lib/charts-theme';
import { DataCard } from './ui/data-card';

interface Props {
  courseSlug: string;
  zoneId: number;
  /** Initial lookback in days; defaults to 90 (matches server default). */
  days?: number;
}

const ALL_INDICES: SatelliteIndex[] = ['ndvi', 'ndre', 'gndvi', 'savi', 'msavi', 'ndwi'];

interface ChartRow {
  date: string;
  ndvi: number | null;
  ndre: number | null;
  gndvi: number | null;
  savi: number | null;
  msavi: number | null;
  ndwi: number | null;
  lowConfidence: boolean;
}

function toRow(obs: SatelliteObservation): ChartRow {
  return {
    date: obs.observationDate,
    ndvi: obs.ndviMedian,
    ndre: obs.ndreMedian,
    gndvi: obs.gndviMedian,
    savi: obs.saviMedian,
    msavi: obs.msaviMedian,
    ndwi: obs.ndwiMedian,
    lowConfidence: obs.isLowConfidence,
  };
}

export function SatelliteTimeline({ courseSlug, zoneId, days = 90 }: Props) {
  const [observations, setObservations] = useState<SatelliteObservation[] | null>(null);
  const [active, setActive] = useState<Set<SatelliteIndex>>(new Set(['ndvi', 'ndre', 'ndwi']));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const now = new Date();
        const from = new Date(now);
        from.setUTCDate(from.getUTCDate() - days);
        const resp = await getObservations(courseSlug, zoneId, {
          from: from.toISOString().slice(0, 10),
          to: now.toISOString().slice(0, 10),
        });
        if (!cancelled) setObservations(resp.observations);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseSlug, zoneId, days]);

  const rows = useMemo<ChartRow[]>(() => (observations ?? []).map(toRow), [observations]);
  const lowConfidenceDates = useMemo(
    () => rows.filter((r) => r.lowConfidence).map((r) => r.date),
    [rows],
  );

  function toggle(idx: SatelliteIndex) {
    setActive((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }

  if (error) return <p className="mt-4 text-sm text-[color:var(--color-error)]">{error}</p>;

  return (
    <DataCard
      className="mt-6"
      title="Satellite timeline"
      subtitle={`Last ${days} days · ${rows.length} acquisition${rows.length === 1 ? '' : 's'}`}
    >
      <div className="flex flex-wrap gap-2">
        {ALL_INDICES.map((idx) => {
          const meta = INDEX_META[idx];
          const isActive = active.has(idx);
          return (
            <button
              key={idx}
              onClick={() => toggle(idx)}
              className={[
                'min-h-[32px] rounded-pill border px-3 py-1 text-xs font-semibold',
                'transition-colors duration-[150ms]',
                isActive ? 'text-[#0b1014]' : 'bg-bg-card text-fg-secondary',
              ].join(' ')}
              style={{
                backgroundColor: isActive ? meta.color : undefined,
                borderColor: meta.color,
              }}
              aria-pressed={isActive}
            >
              {meta.label}
            </button>
          );
        })}
      </div>

      {rows.length === 0 ? (
        <p className="mt-6 text-sm text-fg-muted">
          No satellite observations yet for this range. Trigger a Refresh above, or wait for the
          next scheduled cron (every 6 hours).
        </p>
      ) : (
        <div className="mt-4" style={{ width: '100%', height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={DARK_CHART_TOKENS.gridStroke} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: DARK_CHART_TOKENS.axisColor }}
                stroke={DARK_CHART_TOKENS.axisColor}
                minTickGap={24}
              />
              <YAxis
                domain={[-0.2, 1]}
                tick={{ fontSize: 11, fill: DARK_CHART_TOKENS.axisColor }}
                stroke={DARK_CHART_TOKENS.axisColor}
                width={40}
              />
              <Tooltip
                contentStyle={darkTooltipContentStyle}
                itemStyle={darkTooltipItemStyle}
                labelStyle={darkTooltipLabelStyle}
                formatter={(value, name) => {
                  const v = typeof value === 'number' ? value.toFixed(3) : '—';
                  return [v, String(name)];
                }}
              />
              <Legend wrapperStyle={darkLegendWrapperStyle} />
              {lowConfidenceDates.map((d) => (
                <ReferenceLine
                  key={`lc-${d}`}
                  x={d}
                  stroke={DARK_CHART_TOKENS.orange}
                  strokeDasharray="2 4"
                  strokeWidth={1}
                />
              ))}
              {ALL_INDICES.filter((i) => active.has(i)).map((idx) => (
                <Line
                  key={idx}
                  type="monotone"
                  dataKey={idx}
                  name={INDEX_META[idx].label}
                  stroke={INDEX_META[idx].color}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {lowConfidenceDates.length > 0 && (
        <p className="mt-2 text-xs text-[color:var(--color-warning)]">
          Amber dashed lines mark low-confidence acquisitions (clouds, edge noise, or few pixels).
        </p>
      )}
    </DataCard>
  );
}
